import { T, base } from "../lib/theme";
import { useTheme } from "../lib/ThemeContext";
import Icon from "./Icon";

// ── Section label ──────────────────────────────────────────────────────────
const SectionLabel = ({ label }) => (
  <div style={{
    fontSize: 10, fontWeight: 700, color: T.text3,
    letterSpacing: "0.09em", textTransform: "uppercase",
    padding: "14px 16px 5px",
  }}>
    {label}
  </div>
);

// ── Divider ────────────────────────────────────────────────────────────────
const Divider = () => (
  <div style={{ height: 1, background: T.border1, margin: "6px 12px" }} />
);

// ── Nav item ───────────────────────────────────────────────────────────────
const NavItem = ({ item, active, onClick, badge }) => (
  <div
    className="nav-item"
    onClick={onClick}
    style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 14px", borderRadius: 9, cursor: "pointer",
      marginBottom: 1, fontSize: 13, fontWeight: active ? 600 : 400,
      background: active ? T.indigoDim : "transparent",
      color:      active ? T.indigo    : T.text2,
      userSelect: "none",
    }}
  >
    <div style={{
      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: active ? `${T.indigo}1a` : T.bg2,
      transition: "background 0.15s",
    }}>
      <Icon name={item.icon} size={15} color={active ? T.indigo : T.text3} />
    </div>
    <span style={{ flex: 1 }}>{item.label}</span>
    {badge ? (
      <span style={{
        background: T.rose, color: "#fff",
        borderRadius: 10, fontSize: 10, padding: "1px 7px", fontWeight: 700,
        minWidth: 20, textAlign: "center",
      }}>
        {badge}
      </span>
    ) : null}
  </div>
);

// ── Workspace nav items ────────────────────────────────────────────────────
const LEAD_GEN_ITEMS = [
  { id: "overview",  icon: "overview",  label: "Overview"    },
  { id: "chats",     icon: "chats",     label: "Chats"       },
  { id: "contacts",  icon: "contacts",  label: "Contacts"    },
  { id: "pipeline",  icon: "pipeline",  label: "Lead Gen"    },
  { id: "broadcast", icon: "broadcast", label: "Broadcast"   },
  { id: "schedule",  icon: "schedule",  label: "Schedule"    },
  { id: "knowledge", icon: "knowledge", label: "Knowledge"   },
  { id: "safety",    icon: "shield",    label: "Safety"      },
  { id: "insights",  icon: "activity",  label: "AI Insights" },
  { id: "todo",      icon: "todo",      label: "To Do"       },
  { id: "email",     icon: "mail",      label: "Email"       },
];

const ECOM_ITEMS = [
  { id: "shopify",       icon: "shopify",     label: "Shopify"        },
  { id: "woocommerce",   icon: "woocommerce", label: "WooCommerce"    },
  { id: "ecom_pipeline", icon: "pipeline",    label: "E-com Pipeline" },
];

const OTHER_ITEMS = [
  { id: "messenger", icon: "messenger",  label: "Messenger" },
  { id: "instagram", icon: "instagram",  label: "Instagram" },
];

const BOTTOM_ITEMS = [
  { id: "plan",    icon: "plan", label: "My Plan" },
  { id: "profile", icon: "user", label: "Profile" },
];

export const NAV_ITEMS = [...LEAD_GEN_ITEMS, ...ECOM_ITEMS, ...BOTTOM_ITEMS];

const FEATURE_KEY = {
  broadcast:    "broadcast",
  schedule:     "schedule",
  knowledge:    "knowledge",
  safety:       "safety",
  insights:     "aiInsights",
  contacts:     "contacts",
  pipeline:     "pipeline",
  shopify:      "shopify",
  woocommerce:  "woocommerce",
  ecom_pipeline:"ecomPipeline",
  messenger:    "messenger",
  instagram:    "instagram",
  outbound:     "outbound",
  todo:         "todo",
  email:        "email",
};

// Features hidden by default — shown only when an admin explicitly enables them
// (Feature Access → ON). AUTO/plan-default and OFF both keep them hidden.
const OPTIN_IDS = new Set(["pipeline", "shopify", "woocommerce", "ecom_pipeline", "messenger", "instagram"]);

// ── Main Sidebar ───────────────────────────────────────────────────────────
const Sidebar = ({ page, setPage, status, unread, onLogout, plan, hasEcomStore, features, userRole, memberAccess, mobileOpen, onMobileClose }) => {
  const isOwner = !userRole || userRole === "owner";
  const isFreeLikePlan = plan === "free" || plan === "trial";
  const { isDark, toggle } = useTheme();

  const statusColor = status === "connected" ? T.online
    : (status === "connecting" || status === "qr") ? T.amber : T.rose;

  const statusLabel = status === "connected"   ? "Connected"
    : status === "qr"                          ? "Scan QR"
    : status === "connecting"                  ? "Connecting…"
    : "Offline";

  const allowed = (id) => {
    if (id === "email" && !isOwner) return false;
    if (userRole === "agent" && id === "knowledge") return false;
    if (!isOwner && Array.isArray(memberAccess) && !memberAccess.includes(id)) return false;
    const key = FEATURE_KEY[id];
    // Opt-in features: hidden unless the admin has explicitly turned them ON.
    if (OPTIN_IDS.has(id)) return !!features && features[key] === true;
    if (!key || !features) return true;
    return features[key] !== false;
  };

  const navItem = (item) => (
    <NavItem
      key={item.id}
      item={item}
      active={page === item.id}
      onClick={() => { setPage(item.id); onMobileClose?.(); }}
      badge={item.id === "chats" && unread > 0 ? unread : null}
    />
  );

  const sidebarContent = (
    <div style={{
      width: 228, background: T.bg1,
      borderRight: `1px solid ${T.border1}`,
      display: "flex", flexDirection: "column", flexShrink: 0,
      height: "100%",
    }}>

      {/* ── Logo ──────────────────────────────────────────────────── */}
      <div style={{
        padding: "0 20px",
        height: 56,
        borderBottom: `1px solid ${T.border1}`,
        display: "flex", alignItems: "center",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/logo.svg" alt="Zepliq" style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text1, letterSpacing: "-0.04em", lineHeight: 1.2 }}>Zepliq</div>
            <div style={{ fontSize: 9.5, color: T.text3, fontWeight: 500, letterSpacing: "0.02em", lineHeight: 1 }}>AI Automation</div>
          </div>
        </div>
      </div>

      {/* ── Nav ───────────────────────────────────────────────────── */}
      <nav style={{ flex: 1, padding: "6px 10px", overflowY: "auto" }}>

        <SectionLabel label="Workspace" />
        {LEAD_GEN_ITEMS.filter(item => allowed(item.id)).map(navItem)}

        <Divider />

        {(allowed("shopify") || allowed("woocommerce") || allowed("ecom_pipeline")) && (<>
          <SectionLabel label="E-Commerce" />
          {allowed("shopify")     && navItem(ECOM_ITEMS[0])}
          {allowed("woocommerce") && navItem(ECOM_ITEMS[1])}
          {allowed("ecom_pipeline") && (hasEcomStore
            ? navItem(ECOM_ITEMS[2])
            : (
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 14px", borderRadius: 9, marginBottom: 1,
                color: T.text3, fontSize: 12, cursor: "default", userSelect: "none",
              }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: T.bg2, flexShrink: 0 }}>
                  <Icon name="pipeline" size={15} color={T.text3} />
                </div>
                <span style={{ flex: 1 }}>E-com Pipeline</span>
                <span style={{
                  fontSize: 8, fontWeight: 700, color: T.text3,
                  background: T.bg3, borderRadius: 4, padding: "2px 6px",
                  letterSpacing: "0.04em",
                }}>
                  NO STORE
                </span>
              </div>
            )
          )}
          <Divider />
        </>)}

        {(allowed("messenger") || allowed("instagram")) && (<>
          {/* META section label with Meta infinity logo */}
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "14px 16px 5px",
          }}>
            <svg width="13" height="13" viewBox="0 0 60 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 15C15 10.029 17.686 6 21 6C24.314 6 27 10.029 27 15C27 19.971 24.314 24 21 24C17.686 24 15 19.971 15 15Z" fill="#1877f2"/>
              <path d="M27 15C27 10.029 29.029 6 31.5 6C33.971 6 36 10.029 36 15C36 19.971 33.971 24 31.5 24C29.029 24 27 19.971 27 15Z" fill="#1877f2"/>
              <path d="M36 15C36 10.029 38.686 6 42 6C45.314 6 48 10.029 48 15C48 19.971 45.314 24 42 24C38.686 24 36 19.971 36 15Z" fill="#1877f2"/>
            </svg>
            <span style={{
              fontSize: 10, fontWeight: 700, color: "#1877f2",
              letterSpacing: "0.09em", textTransform: "uppercase",
            }}>Meta</span>
          </div>
          {OTHER_ITEMS.filter(item => allowed(item.id)).map(navItem)}
          <Divider />
        </>)}

        {isOwner && plan !== "free" && allowed("outbound") && (
          <NavItem
            item={{ id: "outbound", icon: "send", label: "Outbound" }}
            active={page === "outbound"}
            onClick={() => { setPage("outbound"); onMobileClose?.(); }}
          />
        )}

        {/* Team Members — owner-only, paid plans (free/trial gated out) */}
        {isOwner && !isFreeLikePlan && allowed("team") && (
          <NavItem
            item={{ id: "team", icon: "contacts", label: "Team Members" }}
            active={page === "team"}
            onClick={() => { setPage("team"); onMobileClose?.(); }}
          />
        )}

        {/* Plan only for owner */}
        {isOwner && navItem(BOTTOM_ITEMS[0])}
        {navItem(BOTTOM_ITEMS[1])}
      </nav>

      {/* ── Bottom bar ────────────────────────────────────────────── */}
      <div style={{ padding: "10px 10px 12px", borderTop: `1px solid ${T.border1}` }}>

        {/* Bot status */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 12px", borderRadius: 9, marginBottom: 6,
          background: T.bg2, border: `1px solid ${T.border1}`,
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
            background: statusColor,
            animation: status === "connected" ? "pulseGlow 2.5s infinite" : "none",
          }} />
          <span style={{ fontSize: 12, color: T.text2, flex: 1, fontWeight: 500 }}>{statusLabel}</span>
        </div>

        {/* Member role badge */}
        {!isOwner && (
          <div style={{ display:"flex", alignItems:"center", gap:7, padding:"7px 12px", borderRadius:9, marginBottom:6, background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.2)" }}>
            <span style={{ fontSize:10, padding:"2px 7px", borderRadius:6, background:"rgba(99,102,241,0.15)", color:"#818cf8", fontWeight:700, textTransform:"capitalize" }}>{userRole}</span>
            <span style={{ fontSize:11, color:T.text3, flex:1 }}>Member access</span>
          </div>
        )}

        {/* Upgrade button — only for free / trial owners */}
        {isOwner && isFreeLikePlan && (
          <div
            onClick={() => setPage("plan")}
            style={{
              display: "flex", alignItems: "center", gap: 9,
              padding: "9px 12px", borderRadius: 9, cursor: "pointer",
              marginBottom: 6,
              background: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))",
              border: "1px solid rgba(99,102,241,0.2)",
              transition: "all 0.15s",
            }}
            onMouseOver={e => e.currentTarget.style.background = "linear-gradient(135deg,rgba(99,102,241,0.2),rgba(139,92,246,0.14))"}
            onMouseOut={e  => e.currentTarget.style.background = "linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.08))"}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="2.2">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.indigo, lineHeight: 1.3 }}>Upgrade</div>
              <div style={{ fontSize: 10, color: T.text3, marginTop: 1 }}>
                {isFreeLikePlan ? `${(plan||"free").charAt(0).toUpperCase()+(plan||"free").slice(1)} plan` : `${(plan||"").charAt(0).toUpperCase()+(plan||"").slice(1)} plan`}
              </div>
            </div>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        )}

        {/* Theme toggle */}
        <div
          onClick={toggle}
          className="nav-item"
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 12px", borderRadius: 9, cursor: "pointer",
            marginBottom: 2,
          }}
        >
          {isDark
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          }
          <span style={{ fontSize: 12, color: T.text2, flex: 1, fontFamily: base.font }}>
            {isDark ? "Light mode" : "Dark mode"}
          </span>
          <div style={{
            width: 30, height: 17, borderRadius: 9,
            background: isDark ? T.border2 : T.indigoDim,
            border: `1px solid ${T.border1}`,
            position: "relative", flexShrink: 0,
            transition: "background 0.2s",
          }}>
            <div style={{
              position: "absolute", top: 3, left: isDark ? 3 : 13,
              width: 9, height: 9, borderRadius: "50%",
              background: isDark ? T.text3 : T.indigo,
              transition: "left 0.2s",
            }} />
          </div>
        </div>

        {/* Sign out */}
        <div
          className="nav-item"
          onClick={onLogout}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 12px", borderRadius: 9, cursor: "pointer",
            color: T.text3, fontSize: 12, fontFamily: base.font,
          }}
        >
          <Icon name="logout" size={14} color={T.text3} />
          Sign out
        </div>
      </div>
    </div>
  );

  // Mobile: overlay drawer
  if (onMobileClose !== undefined) {
    return (
      <>
        {/* Backdrop */}
        {mobileOpen && (
          <div
            onClick={onMobileClose}
            style={{
              position: "fixed", inset: 0, zIndex: 999,
              background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)",
            }}
          />
        )}
        {/* Drawer */}
        <div style={{
          position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 1000,
          transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
          willChange: "transform",
          overflowY: "auto",
        }}>
          {sidebarContent}
        </div>
      </>
    );
  }

  // Desktop: static sidebar
  return sidebarContent;
};

export default Sidebar;
